import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  ClipboardDocumentListIcon,
  MapPinIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { TransportOrderFormValues } from './types';

interface DespatchNoteSummaryPanelProps {
  despatchNoteNumber?: string | null;
  purchaseOrderNumber?: string | null;
  palletTypeWasDefaulted?: boolean;
  skuLines?: {
    name: string;
    quantity: number;
    palletisingType?: string | null;
  }[];
}

function formatDisplayDate(value?: string) {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format('DD MMM YYYY') : value;
}

// Read-only display panel that summarises everything pulled from the despatch
// note. Replaces the Locations / Timing dates / Dimensions+Weight input panels
// so the user reads the source-of-truth instead of staring at greyed-out fields.
export function DespatchNoteSummaryPanel({
  despatchNoteNumber,
  purchaseOrderNumber,
  palletTypeWasDefaulted = false,
  skuLines,
}: DespatchNoteSummaryPanelProps) {
  const { watch } = useFormContext<TransportOrderFormValues>();

  const collectionPostcode = watch('collectionPostcode');
  const deliveryPostcode = watch('deliveryPostcode');
  const pickupCompanyName = watch('pickupCompanyName');
  const pickupFullAddress = watch('pickupFullAddress');
  const dropoffCompanyName = watch('dropoffCompanyName');
  const dropoffFullAddress = watch('dropoffFullAddress');
  const collectionDate = watch('collectionDate');
  const deliveryDate = watch('deliveryDate');
  const totalPallets = watch('totalPallets');
  const palletType = watch('palletType');
  const weight = watch('weight');
  const weightUnit = watch('weightUnit');
  const height = watch('height');
  const heightUnit = watch('heightUnit');
  const briefContents = watch('briefContents');
  const packaging = watch('packaging');
  const bookingReference = watch('bookingReference');

  return (
    <div className='bg-gradient-to-br from-blue-50 to-white rounded-lg border border-blue-200 p-5 flex flex-col gap-y-5'>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex items-start gap-2'>
          <ClipboardDocumentListIcon className='h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0' />
          <div>
            <h3 className='text-base font-semibold text-gray-900'>
              Despatch Note Summary
            </h3>
            <p className='text-xs text-gray-500 mt-0.5'>
              {despatchNoteNumber
                ? `#${despatchNoteNumber}`
                : 'Sourced from supply chain process'}
              {purchaseOrderNumber ? ` · PO ${purchaseOrderNumber}` : ''}
            </p>
          </div>
        </div>
        {bookingReference && (
          <div className='text-right'>
            <p className='text-[11px] uppercase tracking-wide text-gray-500'>
              Booking ref
            </p>
            <p className='text-sm font-medium text-gray-900 max-w-[220px] truncate'>
              {bookingReference}
            </p>
          </div>
        )}
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <div className='flex flex-col gap-1 p-3 rounded-md bg-white border border-gray-100'>
          <div className='flex items-center gap-1.5'>
            <MapPinIcon className='h-3.5 w-3.5 text-blue-500' />
            <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
              Collection
            </p>
          </div>
          {pickupCompanyName && (
            <p className='text-sm font-medium text-gray-900 mt-1'>
              {pickupCompanyName}
            </p>
          )}
          {pickupFullAddress && (
            <p className='text-xs text-gray-600 leading-snug'>
              {pickupFullAddress}
            </p>
          )}
          {collectionPostcode && (
            <p className='text-xs text-gray-500 font-mono mt-0.5'>
              {collectionPostcode}
            </p>
          )}
          {formatDisplayDate(collectionDate) && (
            <div className='flex items-center gap-1.5 mt-2 pt-2 border-t border-gray-100'>
              <CalendarDaysIcon className='h-3.5 w-3.5 text-gray-400' />
              <p className='text-xs text-gray-700'>
                {formatDisplayDate(collectionDate)}
              </p>
            </div>
          )}
        </div>
        <div className='flex flex-col gap-1 p-3 rounded-md bg-white border border-gray-100'>
          <div className='flex items-center gap-1.5'>
            <MapPinIcon className='h-3.5 w-3.5 text-green-500' />
            <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
              Delivery
            </p>
          </div>
          {dropoffCompanyName && (
            <p className='text-sm font-medium text-gray-900 mt-1'>
              {dropoffCompanyName}
            </p>
          )}
          {dropoffFullAddress && (
            <p className='text-xs text-gray-600 leading-snug'>
              {dropoffFullAddress}
            </p>
          )}
          {deliveryPostcode && (
            <p className='text-xs text-gray-500 font-mono mt-0.5'>
              {deliveryPostcode}
            </p>
          )}
          {formatDisplayDate(deliveryDate) && (
            <div className='flex items-center gap-1.5 mt-2 pt-2 border-t border-gray-100'>
              <CalendarDaysIcon className='h-3.5 w-3.5 text-gray-400' />
              <p className='text-xs text-gray-700'>
                {formatDisplayDate(deliveryDate)}
              </p>
            </div>
          )}
        </div>
      </div>

      {(totalPallets || palletType || weight || height) && (
        <div className='grid grid-cols-4 gap-3 p-3 rounded-md bg-white border border-gray-100'>
          {totalPallets && (
            <div className='flex flex-col gap-0.5'>
              <p className='text-[11px] uppercase tracking-wide text-gray-500'>
                Pallets
              </p>
              <p className='text-sm font-semibold text-gray-900'>
                {totalPallets}
              </p>
            </div>
          )}
          {palletType && (
            <div className='flex flex-col gap-0.5'>
              <p className='text-[11px] uppercase tracking-wide text-gray-500'>
                Type
              </p>
              <p className='text-sm font-semibold text-gray-900 truncate'>
                {palletType}
              </p>
              {palletTypeWasDefaulted && (
                <p
                  className='text-[11px] text-amber-700 italic mt-0.5'
                  title='Despatch note did not specify a pallet type — defaulted to Unpalletised. Update the SKU palletisation in the warehouse if this is incorrect.'
                >
                  Defaulted — confirm with warehouse
                </p>
              )}
            </div>
          )}
          {weight && (
            <div className='flex flex-col gap-0.5'>
              <p className='text-[11px] uppercase tracking-wide text-gray-500'>
                Weight
              </p>
              <p className='text-sm font-semibold text-gray-900'>
                {`${weight} ${weightUnit || ''}`.trim()}
              </p>
            </div>
          )}
          {height && (
            <div className='flex flex-col gap-0.5'>
              <p className='text-[11px] uppercase tracking-wide text-gray-500'>
                Max Height
              </p>
              <p className='text-sm font-semibold text-gray-900'>
                {`${height} ${heightUnit || ''}`.trim()}
              </p>
            </div>
          )}
        </div>
      )}

      {(packaging || briefContents) && (
        <div className='grid grid-cols-1 gap-3'>
          {packaging && (
            <div>
              <p className='text-[11px] uppercase tracking-wide text-gray-500'>
                Packaging
              </p>
              <p className='text-sm text-gray-800 mt-0.5'>{packaging}</p>
            </div>
          )}
          {briefContents && (
            <div>
              <p className='text-[11px] uppercase tracking-wide text-gray-500'>
                Contents
              </p>
              <p className='text-sm text-gray-800 mt-0.5 leading-snug'>
                {briefContents}
              </p>
            </div>
          )}
        </div>
      )}

      {skuLines && skuLines.length > 0 && (
        <div className='flex flex-col gap-2 pt-3 border-t border-blue-100'>
          <p className='text-[11px] uppercase tracking-wide text-gray-500'>
            SKUs
          </p>
          <ul className='flex flex-col gap-1'>
            {skuLines.map((line) => (
              <li
                key={`${line.name}|${line.quantity}|${
                  line.palletisingType ?? ''
                }`}
                className='flex items-center justify-between gap-3 text-sm text-gray-800'
              >
                <span className='truncate'>{line.name}</span>
                <span className='text-xs text-gray-500 flex-shrink-0'>
                  {line.quantity}
                  {line.palletisingType ? ` · ${line.palletisingType}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
